<template>
  <div class="about-page bg-base-200 min-h-screen">
    <input
      type="file"
      ref="fileInput"
      @change="handleFileUpload"
      accept="image/*"
      class="hidden"
    />
    <div
      class="hero min-h-[40vh] relative"
      :style="{ backgroundImage: `url(${pageContent.heroImage})` }"
    >
      <div class="hero-overlay bg-opacity-60"></div>
      <div class="hero-content text-center text-neutral-content">
        <div class="max-w-md">
          <h1 class="mb-5 text-5xl font-bold">About Sqwerv</h1>
        </div>
      </div>
      <button
        v-if="loggedIn"
        @click="triggerFileUpload('hero')"
        class="btn btn-primary btn-sm absolute bottom-4 right-4"
        :disabled="isUploading"
      >
        <span
          v-if="isUploading && uploadTarget.type === 'hero'"
          class="loading loading-spinner"
        ></span>
        <span v-else>Change Hero Image</span>
      </button>
      <div
        v-if="isUploading && uploadTarget.type === 'hero'"
        class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      >
        <span class="loading loading-spinner loading-lg"></span>
      </div>
    </div>

    <div class="container mx-auto p-4 md:p-8">
      <div class="card bg-base-100 shadow-xl lg:p-8">
        <div class="card-body">
          <h2 class="card-title text-4xl font-bold mb-6 text-primary">
            Our Story
          </h2>
          <div
            class="prose max-w-none text-base-content text-lg leading-relaxed"
          >
            <p
              v-for="(paragraph, index) in pageContent.story"
              :key="index"
              class="mb-4"
            >
              {{ paragraph }}
            </p>
          </div>
        </div>
      </div>

      <div class="divider my-8 md:my-12">
        <h2 class="text-3xl font-bold text-base-content">Gallery</h2>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div
          v-for="(image, index) in pageContent.galleryImages"
          :key="index"
          class="aspect-w-1 aspect-h-1 relative group"
        >
          <img
            :src="image.src"
            :alt="image.alt"
            class="w-full h-full object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
          />
          <button
            v-if="loggedIn"
            @click="triggerFileUpload('gallery', index)"
            class="btn btn-primary btn-sm absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
            :disabled="isUploading"
          >
            <span
              v-if="
                isUploading &&
                uploadTarget.type === 'gallery' &&
                uploadTarget.index === index
              "
              class="loading loading-spinner"
            ></span>
            <span v-else>Change</span>
          </button>
          <div
            v-if="
              isUploading &&
              uploadTarget.type === 'gallery' &&
              uploadTarget.index === index
            "
            class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg"
          >
            <span class="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useHead } from '@vueuse/head'
import { useStore } from 'vuex'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { db, storage } from '../firebase'
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import imageCompression from 'browser-image-compression'
import { useToast } from '@/composables/useToast'

const store = useStore()
const loggedIn = computed(() => store.getters.loggedIn)
const fileInput = ref(null)
const uploadTarget = ref({ type: null, index: null })
const isUploading = ref(false)
const { showToast } = useToast()

useHead({
  title: 'About - Sqwerv',
  meta: [
    {
      hid: 'sqwervAboutPage',
      name: 'description',
      content:
        'Sqwerv biography, story, origins, and photos. Learn about Guy Frydenlund, Caden Kramer, Jack Marty, and Zach Bulgarelli.',
    },
  ],
})

const pageContent = ref({
  heroImage: '',
  story: [],
  galleryImages: [],
})

onMounted(async () => {
  const docRef = doc(db, 'content', 'aboutPage')
  const docSnap = await getDoc(docRef)
  if (docSnap.exists()) {
    pageContent.value = docSnap.data()
  } else {
    console.log('No such document!')
  }
})

const triggerFileUpload = (type, index = null) => {
  uploadTarget.value = { type, index }
  fileInput.value.click()
}

const handleFileUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  isUploading.value = true

  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  }

  try {
    const compressedFile = await imageCompression(file, options)
    const { type, index } = uploadTarget.value
    const filePath = `about/${type}_${Date.now()}_${compressedFile.name}`
    const imageRef = storageRef(storage, filePath)

    await uploadBytes(imageRef, compressedFile)
    const downloadURL = await getDownloadURL(imageRef)

    const docRef = doc(db, 'content', 'aboutPage')

    if (type === 'hero') {
      await updateDoc(docRef, { heroImage: downloadURL })
      pageContent.value.heroImage = downloadURL
    } else if (type === 'gallery') {
      const updatedGallery = [...pageContent.value.galleryImages]
      updatedGallery[index].src = downloadURL
      await updateDoc(docRef, { galleryImages: updatedGallery })
      pageContent.value.galleryImages = updatedGallery
    }
    showToast('Image uploaded successfully!', 'success')
  } catch (error) {
    console.error('Failed to upload image and update content:', error)
    showToast('Image upload failed. Please try again.', 'error')
  } finally {
    isUploading.value = false
    event.target.value = '' // Clear file input to allow re-uploading the same file
  }
}
</script>

<style scoped>
@import url('https://cdn.jsdelivr.net/npm/@tailwindcss/aspect-ratio@0.4.2/dist/aspect-ratio.min.css');
</style>
